import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'

import { fetchAttachments, uploadAttachment } from 'api/procurement'
import { notify } from 'app/helpers'

// Reusable proof / supporting-doc uploader (P5). Lists existing files (with
// short-lived download links) and uploads a new one straight to S3 via a
// presigned POST. `entityType` is 'pm' | 'pm_line' | 'petty_cash' |
// 'petty_cash_entry'. Scope: pass {centre, month} for PM orders / petty-cash
// allocations, `entry` (a spend-line uuid) for a single petty-cash spend, or
// `line` (a PM order-line uuid) for a per-line discrepancy photo.
// `readOnly` lists the files without any way to add one — HQ viewing a centre's
// bills, where uploading on the centre's behalf would be wrong.
// `autoPreview` opens the first renderable file in the viewer as soon as the list
// loads — for callers whose button means "show me the bill", not "manage files".

// What the viewer can render in place. Prefer the recorded content type and fall
// back to the extension, since rows stored before content_type was captured have
// an empty one.
const kindOf = (a) => {
  const type = (a && a.content_type) || ''
  const name = ((a && a.name) || '').toLowerCase()
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(name)) {
    return 'image'
  }
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  return 'other'
}
const AttachmentUploader = ({
  entityType,
  centre,
  month,
  entry,
  line,
  label = 'Attachments',
  disabled = false,
  readOnly = false,
  autoPreview = false
}) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null) // the file open in the viewer
  const inputRef = useRef(null)

  const scope = line ? { line } : entry ? { entry } : { centre, month }
  const ready = line ? !!line : entry ? !!entry : !!centre && !!month

  const load = async () => {
    if (!ready) return
    setLoading(true)
    try {
      const res = await fetchAttachments(entityType, scope)
      const results = res.results || []
      setItems(results)
      // Jump straight to the bill when the caller asked to view rather than
      // manage. 'other' types have nothing to render, so leave the list showing.
      if (autoPreview) {
        const first = results.find((a) => kindOf(a) !== 'other')
        if (first) setPreview(first)
      }
    } catch (e) {
      // The entity may not exist yet (e.g. allocation not saved). Show empty.
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  // Reload whenever the target entity changes.
  // eslint-disable-next-line
  useEffect(() => {
    load()
  }, [entityType, centre, month, entry, line])

  const onPick = () => inputRef.current && inputRef.current.click()

  const onFile = async (e) => {
    // Multiple images / PDFs can be selected at once; upload them in turn.
    const files = Array.from(e.target.files || [])
    e.target.value = '' // let the same file be re-selected after an error
    if (!files.length) return
    setBusy(true)
    let last
    let done = 0
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        last = await uploadAttachment(entityType, scope, file)
        done += 1
      }
      if (last) setItems(last.results || [])
      notify(`${done} file${done === 1 ? '' : 's'} attached`, '', 'success')
    } catch (err) {
      notify((err && err.message) || 'Upload failed', '', 'error')
      load() // show whatever did upload before the failure
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <div style={{ margin: '4px 0' }}>
        {loading && (
          <Typography variant="body2" color="textSecondary">
            Loading…
          </Typography>
        )}
        {!loading && items.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            {readOnly ? 'No files attached.' : 'No files yet.'}
          </Typography>
        )}
        {items.map((a) => (
          <div key={a.uuid}>
            {a.url || a.view_url ? (
              <Link
                component="button"
                type="button"
                align="left"
                onClick={() => setPreview(a)}>
                {a.name}
              </Link>
            ) : (
              a.name
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={onFile}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={onPick}
            disabled={disabled || busy}>
            {busy ? <CircularProgress size={16} /> : '+ Attach files'}
          </Button>
        </>
      )}

      {/* View a bill in place rather than downloading it. `view_url` is served
          inline; it falls back to the download URL so the page still works if
          the frontend ships ahead of the API. */}
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{preview ? preview.name : ''}</DialogTitle>
        <DialogContent dividers>
          {preview && kindOf(preview) === 'image' && (
            <Box display="flex" justifyContent="center">
              <img
                src={preview.view_url || preview.url}
                alt={preview.name}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
            </Box>
          )}
          {preview && kindOf(preview) === 'pdf' && (
            <iframe
              src={preview.view_url || preview.url}
              title={preview.name}
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          )}
          {preview && kindOf(preview) === 'other' && (
            <Typography variant="body2" color="textSecondary">
              This file type can&apos;t be previewed. Use Download to open it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {preview && (preview.url || preview.view_url) && (
            <Button
              color="primary"
              href={preview.url || preview.view_url}
              target="_blank"
              rel="noopener noreferrer">
              Download
            </Button>
          )}
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AttachmentUploader
